import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";
import { useAttorneyAutocompleteSearch } from "../../../../features/bonds/shared/utils/attorneySearch";
import { createBondAttorneySearchErrorHandler } from "../../../../features/bonds/shared/utils/bondAttorneySearchWiring";
import { useBondRetrievalGuardianDropdowns } from "../hooks/useBondRetrievalGuardianDropdowns";
import { useRetrievalGuardianFormSetup } from "../hooks/useRetrievalGuardianFormSetup";
import {
  getRetrievalAttorneyLawFirmId,
  RETRIEVAL_GUARDIAN_CONFIG,
} from "../guardianFormConfig";
import GuardianFormBody from "./GuardianFormBody";

const scrollToTop = () => {
  if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

function RetrievalGuardianFormCore({
  bondKind,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  splitEmploymentOccupation,
  joinEmploymentOccupation,
  data: dataProp,
  updateData,
  globalData,
  role,
  isDisabled,
  pii_access,
  attorneyLawFirmId,
  enableGuardianshipEffects = false,
  userRole,
  pgId,
}) {
  const data = dataProp ?? { guardians: [] };
  const retrievalCfg = RETRIEVAL_GUARDIAN_CONFIG[bondKind];
  const searchRole = userRole ?? role ?? null;

  const dropdowns = useBondRetrievalGuardianDropdowns({
    attorneyLawFirmId,
    mapProfessionalGuardian: retrievalCfg.mapProfessionalGuardian,
    attorneyFetchMode: "search",
  });

  const onAttorneySearchError = useCallback(
    createBondAttorneySearchErrorHandler(toast),
    []
  );

  const attorneySearch = useAttorneyAutocompleteSearch({
    apiClient,
    role: searchRole,
    lawfirmId: attorneyLawFirmId,
    onSearchError: onAttorneySearchError,
  });

  const [attorneySearchInput, setAttorneySearchInput] = useState("");

  const mergedAttorneys = attorneySearch.attorneys;
  const mergedLoadingAttorneys = attorneySearch.loadingAttorneys;

  const { showSSNStates, toggleShowSSN, handlers } = useRetrievalGuardianFormSetup({
    bondKind,
    data,
    updateData,
    attorneys: mergedAttorneys,
    joinEmploymentOccupation,
  });

  const { fetchProfessionalGuardianDetails } = handlers;

  useEffect(() => {
    if (!enableGuardianshipEffects) return undefined;
    scrollToTop();
  }, [enableGuardianshipEffects]);

  useEffect(() => {
    if (!enableGuardianshipEffects || userRole || !pgId) return undefined;
    fetchProfessionalGuardianDetails(0, pgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableGuardianshipEffects, userRole, pgId, dropdowns.professionalGuardians]);

  return (
    <GuardianFormBody
      bondKind={bondKind}
      globalData={globalData}
      data={data}
      updateData={updateData}
      role={role}
      isDisabled={isDisabled}
      pii_access={pii_access}
      allCities={dropdowns.allCities}
      allStates={dropdowns.allStates}
      professionalGuardians={dropdowns.professionalGuardians}
      attorneys={mergedAttorneys}
      loadingCitiesStates={dropdowns.loadingCitiesStates}
      loadingGuardians={dropdowns.loadingGuardians}
      loadingAttorneys={mergedLoadingAttorneys}
      attorneySearchEnabled
      attorneySearchInput={attorneySearchInput}
      setAttorneySearchInput={setAttorneySearchInput}
      debouncedSearchAttorneys={attorneySearch.debouncedSearch}
      clearAttorneys={attorneySearch.clearAttorneys}
      showSSNStates={showSSNStates}
      toggleShowSSN={toggleShowSSN}
      GoogleAutoComplete={GoogleAutoComplete}
      LabeledCreatableSelect={LabeledCreatableSelect}
      splitEmploymentOccupation={splitEmploymentOccupation}
      handlers={handlers}
    />
  );
}

function RetrievalGuardianFormWithRedux(props) {
  const { role: userRole } = useSelector((state) => state.auth);
  const pgId = useSelector((state) => state.pg.pgId);
  const lawfirm = useSelector((state) => state.lawfirm);

  const attorneyLawFirmId = useMemo(
    () => getRetrievalAttorneyLawFirmId(userRole, lawfirm),
    [userRole, lawfirm]
  );

  return (
    <RetrievalGuardianFormCore
      {...props}
      attorneyLawFirmId={attorneyLawFirmId}
      enableGuardianshipEffects
      userRole={userRole}
      pgId={pgId}
    />
  );
}

/**
 * Shared bond-retrieval guardian principals step (Phase 9.9G).
 */
export default function RetrievalGuardianFormBase(props) {
  const retrievalCfg = RETRIEVAL_GUARDIAN_CONFIG[props.bondKind];
  if (retrievalCfg.useReduxAttorneyLawFirmId) {
    return <RetrievalGuardianFormWithRedux {...props} />;
  }
  return <RetrievalGuardianFormCore {...props} attorneyLawFirmId={0} />;
}

RetrievalGuardianFormCore.propTypes = {
  bondKind: PropTypes.string.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  splitEmploymentOccupation: PropTypes.func.isRequired,
  joinEmploymentOccupation: PropTypes.func.isRequired,
  data: PropTypes.shape({
    guardians: PropTypes.array,
  }),
  updateData: PropTypes.func.isRequired,
  globalData: PropTypes.object,
  role: PropTypes.string,
  isDisabled: PropTypes.bool,
  pii_access: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  attorneyLawFirmId: PropTypes.number.isRequired,
  enableGuardianshipEffects: PropTypes.bool,
  userRole: PropTypes.string,
  pgId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

RetrievalGuardianFormBase.propTypes = {
  bondKind: PropTypes.string.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  splitEmploymentOccupation: PropTypes.func.isRequired,
  joinEmploymentOccupation: PropTypes.func.isRequired,
  data: PropTypes.shape({
    guardians: PropTypes.array,
  }),
  updateData: PropTypes.func.isRequired,
  globalData: PropTypes.shape({
    bond_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    basicInformation: PropTypes.shape({
      PrincipalNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      GuardianNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      bondAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      is_esignatures: PropTypes.string,
    }),
  }),
  role: PropTypes.string,
  isDisabled: PropTypes.bool,
  pii_access: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};
